import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { SettingsNav } from '../components/SettingsNav';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Toast } from '../components/Toast';
import { SkeletonRow } from '../components/SkeletonRow';
import { useAuth } from '../lib/auth';
import { fmt } from '../lib/format';
import {
  changeTeamRole,
  deactivateTeamMember,
  getTeam,
  inviteTeamMembers,
  restoreTeamMember,
  resendTeamInvite,
  revokeTeamInvite,
} from '../lib/api';
import {
  STATUS_PILL,
  countByStatus,
  displayName,
  filterMembers,
  initialsFor,
  toGuard,
  type TeamFilter,
} from '../lib/team';
import {
  ACCESS_MATRIX,
  ROLE_INFO,
  ROLE_PICKER_ORDER,
  checkInviteEmail,
  deactivateProblem,
  describeActivity,
  inviteProblemMessage,
  parseEmailList,
  revokeProblem,
  roleChangeProblem,
  type DashboardRole,
} from '../../../src/services/team/roles';
import type { TeamInviteResult, TeamMember } from '../types';

const FILTERS: Array<{ key: TeamFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'invited', label: 'Invited' },
  { key: 'deactivated', label: 'Deactivated' },
];

export function Team(): JSX.Element {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({ queryKey: ['team'], queryFn: getTeam });
  const [filter, setFilter] = useState<TeamFilter>('all');
  const [query, setQuery] = useState('');
  const [inviting, setInviting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const members = data?.members ?? [];
  const counts = countByStatus(members);
  const rows = filterMembers(members, filter, query);
  const selected = members.find((m) => m.id === selectedId) ?? null;

  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });
  const done = (text: string) => {
    setToast({ kind: 'ok', text });
    void refresh();
  };

  return (
    <div className="ari-app">
      <TopNav />
      {/* team-page pins the drawer, modal and toast to the viewport: the
          document scrolls on this page, so absolute overlays would sit at the
          top of the page instead of on screen. */}
      <div className="page team-page">
        <div className="page-hd">
          <div>
            <div className="crumb"><Link to="/settings">Settings</Link> / Team</div>
            <h1 className="d-title">Team</h1>
            <div className="d-meta" style={{ marginTop: 6, fontSize: 14 }}>
              Invite people, choose what they can see, and turn off access when someone leaves.
            </div>
          </div>
          <Button variant="primary" icon="plus" onClick={() => setInviting(true)} disabled={!data}>
            Invite people
          </Button>
        </div>

        <div className="settings-grid team-grid">
          <SettingsNav />
          <div className="settings-content">
            <section className="settings-card">
              <div className="settings-card-hd">
                <div>
                  <h2 className="ttl">People with access</h2>
                  <p className="sub">Everyone who can sign in to the Operations Dashboard. Only admins see this page.</p>
                </div>
              </div>
              <div className="toolbar team-toolbar">
                <div className="left">
                  <div className="segmented" role="group" aria-label="Filter by status">
                    {FILTERS.map((f) => (
                      <button key={f.key} type="button" className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>
                        {f.label}<span className="seg-n">{counts[f.key]}</span>
                      </button>
                    ))}
                  </div>
                  <input
                    className="input"
                    type="search"
                    aria-label="Search people"
                    placeholder="Search name or email"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ width: 240 }}
                  />
                </div>
                <div className="right">
                  <span className="count">
                    <strong>{counts.active + counts.invited}</strong> people · {counts.invited} invite{counts.invited === 1 ? '' : 's'} pending
                  </span>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="tbl team-tbl">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Role</th>
                      <th>Can see</th>
                      <th>Status</th>
                      <th>Last sign-in</th>
                      <th><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cells={6} />)}
                    {error && (
                      <tr><td colSpan={6} className="team-empty">Could not load the team: {(error as Error).message}</td></tr>
                    )}
                    {data && rows.length === 0 && (
                      <tr><td colSpan={6} className="team-empty">No one matches that filter.</td></tr>
                    )}
                    {rows.map((m) => (
                      <tr key={m.id} className={m.status === 'deactivated' ? 'off' : ''}>
                        <td>
                          <div className="team-person">
                            <span className={`team-av ${m.status === 'invited' ? 'pending' : m.status === 'deactivated' ? 'off' : ''}`} aria-hidden="true">
                              {initialsFor(m)}
                            </span>
                            <div className="team-person-txt">
                              <div className="nm">
                                <span className="t">{displayName(m)}</span>
                                {m.id === user?.id && <span className="team-tag">You</span>}
                                {m.external && <span className="team-tag">External</span>}
                              </div>
                              <div className="em">
                                {m.status === 'invited'
                                  ? `Invite sent ${fmt.date(m.invitedAt)}${m.invitedByName ? ` by ${m.invitedByName}` : ''}`
                                  : m.name ? m.email : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{ROLE_INFO[m.role].label}</td>
                        <td className="muted">{m.status === 'deactivated' ? 'Nothing' : ROLE_INFO[m.role].access}</td>
                        <td><span className={`pill ${STATUS_PILL[m.status].cls}`}><span className="pdot" />{STATUS_PILL[m.status].label}</span></td>
                        <td className="d-tnum">{m.lastSignInAt ? fmt.date(m.lastSignInAt) : <span className="muted">Not yet</span>}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button size="sm" variant="ghost" aria-label={`Manage ${displayName(m)}`} onClick={() => setSelectedId(m.id)}>
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <RolesCard />

            <section className="settings-card">
              <div className="settings-card-hd">
                <div>
                  <h2 className="ttl">Recent changes</h2>
                  <p className="sub">Invites, role changes and deactivations, newest first.</p>
                </div>
              </div>
              <div className="team-activity">
                {(data?.activity ?? []).length === 0 && <div className="act-row"><span /><span className="d-meta">No changes yet.</span></div>}
                {(data?.activity ?? []).slice(0, 20).map((a) => (
                  <div key={a.id} className="act-row">
                    <span className="when">{fmt.date(a.at)}</span>
                    <span>{describeActivity(a)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {inviting && data && (
          <InviteModal
            members={members}
            orgDomains={data.orgDomains}
            onClose={() => setInviting(false)}
            onInvited={(n) => {
              if (n > 0) done(n === 1 ? 'Invite sent' : `${n} invites sent`);
              else void refresh();
            }}
          />
        )}

        {selected && (
          <MemberDrawer
            member={selected}
            members={members}
            actorId={user?.id ?? ''}
            onClose={() => setSelectedId(null)}
            onDone={(text, close) => {
              done(text);
              if (close) setSelectedId(null);
            }}
            onError={(text) => setToast({ kind: 'err', text })}
          />
        )}

        {toast && <Toast kind={toast.kind}>{toast.text}</Toast>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles and access
// ---------------------------------------------------------------------------

function RolesCard(): JSX.Element {
  return (
    <section className="settings-card">
      <div className="settings-card-hd">
        <div>
          <h2 className="ttl">Roles and access</h2>
          <p className="sub">
            What each role can do. Project Manager is new: it gives PMs SOWs and nothing else, so it can widen once they are using it.
          </p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl team-matrix">
          <thead>
            <tr>
              <th scope="col">Area</th>
              {ROLE_PICKER_ORDER.map((r) => (
                <th key={r} scope="col" className={r === 'pm' ? 'pm' : ''}>
                  {ROLE_INFO[r].label}
                  {r === 'pm' && <span className="pill new" style={{ marginLeft: 6 }}>New</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="roledef">
              <td>In short</td>
              {ROLE_PICKER_ORDER.map((r) => <td key={r} className={r === 'pm' ? 'pm' : ''}>{ROLE_INFO[r].summary}</td>)}
            </tr>
            {ACCESS_MATRIX.map((area) => (
              <tr key={area.key}>
                <td>
                  <div className="area">{area.label}</div>
                  <div className="area-sub">{area.detail}</div>
                </td>
                {ROLE_PICKER_ORDER.map((r) => (
                  <td key={r} className={`${r === 'pm' ? 'pm' : ''} ${area.access[r] ? '' : 'no'}`}>
                    {area.access[r] ?? 'No access'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Invite
// ---------------------------------------------------------------------------

interface InviteModalProps {
  members: TeamMember[];
  orgDomains: string[];
  onClose(): void;
  onInvited(count: number): void;
}

function InviteModal({ members, orgDomains, onClose, onInvited }: InviteModalProps): JSX.Element {
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState<DashboardRole>('viewer');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TeamInviteResult[]>([]);
  const [sending, setSending] = useState(false);
  const domainHint = orgDomains.map((d) => `@${d}`).join(' or ');

  const onTeam = useMemo(
    () => new Set(members.filter((m) => m.status !== 'deactivated').map((m) => m.email.toLowerCase())),
    [members],
  );

  /** Adds valid addresses from the draft; returns the full list (state may lag). */
  function addDraft(): string[] {
    const next = [...emails];
    let problem: string | null = null;
    for (const email of parseEmailList(draft)) {
      const p = checkInviteEmail(email, orgDomains);
      if (p) { problem ??= inviteProblemMessage(email, p, orgDomains); continue; }
      if (onTeam.has(email)) { problem ??= `${email} is already on the team.`; continue; }
      if (!next.includes(email)) next.push(email);
    }
    setEmails(next);
    setError(problem);
    if (!problem) setDraft('');
    return next;
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDraft();
    }
  }

  async function send() {
    const list = draftValid.length > 0 ? addDraft() : emails;
    if (list.length === 0) return;
    setSending(true);
    setResults([]);
    try {
      const out = await inviteTeamMembers({ emails: list, role, note: note.trim() || undefined });
      const invited = out.filter((r) => r.outcome === 'invited').length;
      const problems = out.filter((r) => r.outcome !== 'invited');
      onInvited(invited);
      if (problems.length === 0) {
        onClose();
        return;
      }
      // Keep the modal open with only the addresses that still need attention.
      setResults(problems);
      setEmails(problems.map((p) => p.email));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  // Count only addresses that will actually be sent: chips plus valid ones still in the box.
  const draftValid = parseEmailList(draft).filter(
    (e) => !checkInviteEmail(e, orgDomains) && !onTeam.has(e) && !emails.includes(e),
  );
  const count = emails.length + draftValid.length;

  return (
    <Modal onClose={onClose} size="md">
      <div className="modal-hd">
        <div className="eb">Team</div>
        <div className="ttl">Invite people</div>
      </div>
      <div className="modal-bd">
        <div>
          <label className="input-label" htmlFor="invite-email">Email addresses</label>
          <div className="team-email-row">
            <input
              id="invite-email"
              className="input"
              type="email"
              placeholder={orgDomains[0] ? `name@${orgDomains[0]}` : 'name@company.com'}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setError(null); }}
              onKeyDown={onKey}
              autoFocus
            />
            <Button variant="secondary" onClick={() => addDraft()}>Add</Button>
          </div>
          {error
            ? <div className="input-error">{error}</div>
            : <div className="d-meta" style={{ marginTop: 6 }}>{domainHint} addresses only. Paste several at once, separated by commas.</div>}
          {emails.length > 0 && (
            <div className="team-chips">
              {emails.map((e) => (
                <span key={e} className="team-chip">
                  {e}
                  <button type="button" aria-label={`Remove ${e}`} onClick={() => setEmails(emails.filter((x) => x !== e))}>
                    <Icon name="x" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <fieldset className="team-fieldset">
          <legend className="input-label">Role</legend>
          <div className="team-role-opts">
            {ROLE_PICKER_ORDER.map((r) => (
              <label key={r} className="team-role-opt">
                <input type="radio" name="invite-role" checked={role === r} onChange={() => setRole(r)} />
                <span>
                  <span className="rn">{ROLE_INFO[r].label}{r === 'pm' && <span className="pill new">New</span>}</span>
                  <span className="rd">{ROLE_INFO[r].summary}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="input-label" htmlFor="invite-note">Add a note <span className="d-meta">(optional)</span></label>
          <textarea
            id="invite-note"
            className="input team-textarea"
            placeholder="Shows in the invite email."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
        </div>

        {results.length > 0 ? (
          <div className="banner warn team-banner">
            <span className="dot" />
            <div>
              {results.map((r) => <div key={r.email}>{r.message ?? `${r.email}: not invited.`}</div>)}
            </div>
          </div>
        ) : (
          <div className="banner team-banner">
            <span className="dot" style={{ color: 'var(--navy-500)' }} />
            <span>Each person gets an email with a link to set a password. Links expire, so resend from this page if they miss it.</span>
          </div>
        )}
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={() => void send()} disabled={sending || count === 0}>
          {sending ? <><span className="spinner" />Sending…</> : count > 1 ? `Send ${count} invites` : 'Send invite'}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Member drawer
// ---------------------------------------------------------------------------

interface MemberDrawerProps {
  member: TeamMember;
  members: TeamMember[];
  actorId: string;
  onClose(): void;
  onDone(text: string, close: boolean): void;
  onError(text: string): void;
}

function MemberDrawer({ member, members, actorId, onClose, onDone, onError }: MemberDrawerProps): JSX.Element {
  const [draftRole, setDraftRole] = useState<DashboardRole>(member.role);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraftRole(member.role);
    setConfirming(false);
  }, [member.id, member.role]);

  useEffect(() => {
    const onEsc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const guards = members.map(toGuard);
  const target = toGuard(member);
  const invited = member.status === 'invited';
  const off = member.status === 'deactivated';
  const name = displayName(member);

  // Is the role picker usable at all? (Ask about a different role than theirs.)
  const lockReason =
    member.id === actorId ? 'This is you. Another admin has to change your role.'
    : off ? 'Restore their access to change their role.'
    : roleChangeProblem({ actorId, target, nextRole: member.role === 'viewer' ? 'generator' : 'viewer', members: guards });
  const saveProblem = draftRole === member.role ? null : roleChangeProblem({ actorId, target, nextRole: draftRole, members: guards });
  const removeProblem = invited ? revokeProblem(target) : deactivateProblem({ actorId, target, members: guards });
  const canRemove = !off && member.id !== actorId && !removeProblem;

  async function run(action: () => Promise<void>, text: string, close: boolean) {
    setBusy(true);
    try {
      await action();
      onDone(text, close);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="member-title" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-hd">
          <div className="team-who">
            <span className={`team-av lg ${invited ? 'pending' : off ? 'off' : ''}`} aria-hidden="true">{initialsFor(member)}</span>
            <div style={{ minWidth: 0 }}>
              <div className="eb">Team member</div>
              <div className="nm" id="member-title">{name}</div>
            </div>
          </div>
          <button type="button" className="btn btn-icon" aria-label="Close" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        <div className="drawer-bd">
          <div className="drawer-section">
            <dl className="kv team-kv">
              <dt>Email</dt><dd className="mono">{member.email}</dd>
              <dt>Status</dt>
              <dd><span className={`pill ${STATUS_PILL[member.status].cls}`}><span className="pdot" />{STATUS_PILL[member.status].label}</span></dd>
              {invited ? (
                <><dt>Invited</dt><dd>{fmt.date(member.invitedAt)}{member.invitedByName ? ` by ${member.invitedByName}` : ''}</dd></>
              ) : (
                <><dt>Last sign-in</dt><dd>{member.lastSignInAt ? fmt.dateTime(member.lastSignInAt) : 'Not yet'}</dd></>
              )}
              {member.external && <><dt>Domain</dt><dd>Outside Ariel</dd></>}
            </dl>
          </div>

          <div className="drawer-section">
            <label className="drawer-section-h" htmlFor="member-role" style={{ display: 'block' }}>Role</label>
            <select
              id="member-role"
              className="select"
              style={{ width: '100%' }}
              value={draftRole}
              onChange={(e) => setDraftRole(e.target.value as DashboardRole)}
              disabled={Boolean(lockReason) || busy}
            >
              {ROLE_PICKER_ORDER.map((r) => <option key={r} value={r}>{ROLE_INFO[r].label}</option>)}
            </select>
            <div className={saveProblem ? 'input-error' : 'd-meta'} style={{ marginTop: 6 }}>
              {saveProblem ?? lockReason ?? ROLE_INFO[draftRole].summary}
            </div>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">What they can see</div>
            <div className="team-access">
              {ACCESS_MATRIX.map((a) => {
                const level = off ? null : a.access[draftRole];
                return (
                  <div key={a.key} className={`team-access-row ${level ? '' : 'no'}`}>
                    <span>{a.label}</span>
                    <span className="lvl">{level ?? 'No access'}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {canRemove && (
            <div className="drawer-section">
              <div className="drawer-section-h">{invited ? 'Cancel invite' : 'Turn off access'}</div>
              {!confirming ? (
                <div className="team-danger">
                  <span className="d-meta">
                    {invited
                      ? member.lastSignInAt
                        ? 'They have already signed in. Revoking turns off access and keeps their history. You can restore access later.'
                        : 'The link in their email stops working. You can invite them again later.'
                      : 'Signs them out and blocks sign-in. Their SOWs and history stay.'}
                  </span>
                  <Button size="sm" variant="secondary" className="risk" onClick={() => setConfirming(true)}>
                    {invited ? 'Revoke invite' : 'Deactivate'}
                  </Button>
                </div>
              ) : (
                <div className="banner warn team-confirm">
                  <div>
                    {invited
                      ? member.lastSignInAt
                        ? `Revoke the invite to ${member.email}? Their access will be turned off and their history kept.`
                        : `Revoke the invite to ${member.email}? The link in their email stops working.`
                      : `Deactivate ${name}? They lose access right away and cannot sign back in. Their SOWs and history stay on the dashboard.`}
                  </div>
                  <div className="team-confirm-actions">
                    <Button size="sm" variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>Keep access</Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() =>
                        invited
                          ? void run(() => revokeTeamInvite(member.id), 'Invite revoked', true)
                          : void run(() => deactivateTeamMember(member.id), `Access turned off for ${name}`, true)
                      }
                    >
                      {invited ? 'Revoke invite' : 'Deactivate'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="drawer-foot">
          {invited && (
            <Button
              variant="secondary"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => void run(() => resendTeamInvite(member.id), `Invite resent to ${member.email}`, false)}
            >
              Resend invite
            </Button>
          )}
          {off && (
            <Button
              variant="secondary"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => void run(() => restoreTeamMember(member.id), `Access restored for ${name}`, false)}
            >
              Restore access
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            disabled={busy || draftRole === member.role || Boolean(saveProblem) || Boolean(lockReason)}
            onClick={() => void run(() => changeTeamRole(member.id, draftRole), `Role updated for ${name}`, true)}
          >
            Save changes
          </Button>
        </div>
      </aside>
    </div>
  );
}
